import { StudioQuiz } from './quizTypes';

/**
 * Brouillons du studio quiz, gardés dans ce navigateur (localStorage).
 *
 * Pourquoi : l'éditeur ne gardait rien tant que « Enregistrer » n'avait pas
 * abouti (le 15/10/2026 une animatrice a perdu ~2 h de questions). Chaque
 * modification est recopiée ici ; le brouillon disparaît quand Firestore
 * confirme l'enregistrement, ou quand l'animateur le supprime depuis « Mes quiz ».
 * Même principe que la régie du jeu UnQuiz (unquizz-v2 `regieDraft.ts`).
 */

// Pas de plafond : un brouillon ne disparaît jamais tout seul (quelques Ko chacun,
// le stockage plein se voit à `saveDraft` = false).
const KEY_PREFIX = 'unboared.quizDrafts';

/**
 * `baseUpdatedAt` = date (ms) de la version enregistrée d'où part le brouillon
 * (null pour un quiz jamais enregistré) : sert à repérer un brouillon périmé.
 */
export type QuizDraft = { quiz: StudioQuiz; savedAt: number; isNew: boolean; baseUpdatedAt?: number | null };

const keyFor = (clientID: string) => `${KEY_PREFIX}:${clientID}`;

function read(clientID: string): QuizDraft[] {
    try {
        const raw = localStorage.getItem(keyFor(clientID));
        if (!raw) return [];
        const list = JSON.parse(raw);
        if (!Array.isArray(list)) return [];
        return list.filter(
            (d): d is QuizDraft =>
                !!d &&
                typeof d.savedAt === 'number' &&
                !!d.quiz &&
                typeof d.quiz.id === 'string' &&
                Array.isArray(d.quiz.questions)
        );
    } catch {
        return [];
    }
}

function write(clientID: string, list: QuizDraft[]): boolean {
    try {
        if (list.length === 0) localStorage.removeItem(keyFor(clientID));
        else localStorage.setItem(keyFor(clientID), JSON.stringify(list));
        return true;
    } catch {
        return false;
    }
}

/** Brouillons de l'établissement, le plus récent d'abord. */
export function listDrafts(clientID: string): QuizDraft[] {
    return read(clientID).sort((a, b) => b.savedAt - a.savedAt);
}

export function getDraft(clientID: string, quizId: string): QuizDraft | null {
    return read(clientID).find((d) => d.quiz.id === quizId) ?? null;
}

/** false = stockage indisponible (navigation privée, plein). */
export function saveDraft(
    clientID: string,
    quiz: StudioQuiz,
    isNew: boolean,
    baseUpdatedAt: number | null = null,
    now = Date.now()
): boolean {
    const others = read(clientID).filter((d) => d.quiz.id !== quiz.id);
    const next = [{ quiz, savedAt: now, isNew, baseUpdatedAt }, ...others.sort((a, b) => b.savedAt - a.savedAt)];
    return write(clientID, next);
}

export function deleteDraft(clientID: string, quizId: string): void {
    const list = read(clientID);
    const next = list.filter((d) => d.quiz.id !== quizId);
    if (next.length !== list.length) write(clientID, next);
}

/** Un brouillon vaut la peine d'être gardé s'il contient quelque chose de tapé. */
export function draftHasContent(quiz: StudioQuiz): boolean {
    if (quiz.title.trim() || quiz.description?.trim()) return true;
    // Vrai/Faux pré-remplit ses deux réponses : seules comptent celles tapées.
    return quiz.questions.some((q) => q.question.trim() || (q.type !== 'vf' && q.options.some((o) => o.trim())));
}

/** Date Firestore (Timestamp, Date, nombre, texte) en ms. */
export function timeMs(v: unknown): number | null {
    if (v == null) return null;
    if (v instanceof Date) return v.getTime();
    if (typeof v === 'number') return v;
    if (typeof v === 'string') {
        const t = Date.parse(v);
        return Number.isNaN(t) ? null : t;
    }
    if (typeof v === 'object') {
        const o = v as { seconds?: number; toMillis?: () => number };
        if (typeof o.toMillis === 'function') return o.toMillis();
        if (typeof o.seconds === 'number') return o.seconds * 1000;
    }
    return null;
}

/**
 * Le quiz a été réenregistré ailleurs (TV, autre appareil) depuis que ce
 * brouillon a été commencé : la version enregistrée gagne. Compare les dates
 * de version Firestore entre elles, jamais à l'horloge de l'appareil.
 */
export function draftIsStale(draft: QuizDraft, savedUpdatedAt: unknown): boolean {
    const savedAt = timeMs(savedUpdatedAt);
    const baseAt = draft.baseUpdatedAt ?? null;
    return savedAt !== null && baseAt !== null && savedAt > baseAt + 1000;
}

/** JSON aux clés triées : même empreinte quel que soit l'ordre des champs (Firestore ne le garde pas). */
function stableStringify(value: unknown): string {
    if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
    if (value && typeof value === 'object') {
        const entries = Object.entries(value as Record<string, unknown>)
            .filter(([, v]) => v !== undefined)
            .sort(([a], [b]) => a.localeCompare(b, 'en'));
        return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${stableStringify(v)}`).join(',')}}`;
    }
    return JSON.stringify(value) ?? 'null';
}

/** Sérialisation des champs édités : sert à savoir si le quiz a changé depuis son ouverture. */
export function editableFingerprint(quiz: StudioQuiz): string {
    return stableStringify({
        title: quiz.title,
        description: quiz.description ?? '',
        category: quiz.category ?? '',
        coverImage: quiz.coverImage ?? '',
        scoringType: quiz.scoringType ?? 'speed',
        lossEnabled: quiz.lossEnabled ?? false,
        showOnHomepage: quiz.showOnHomepage ?? true,
        questions: quiz.questions,
    });
}

/** « il y a 5 min », « hier »… */
export function timeAgo(ts: number): string {
    const minutes = Math.round((ts - Date.now()) / 60000);
    try {
        const rtf = new Intl.RelativeTimeFormat('fr', { numeric: 'auto' });
        if (minutes === 0) return rtf.format(0, 'second');
        if (Math.abs(minutes) < 60) return rtf.format(minutes, 'minute');
        const hours = Math.round(minutes / 60);
        if (Math.abs(hours) < 24) return rtf.format(hours, 'hour');
        return rtf.format(Math.round(hours / 24), 'day');
    } catch {
        return new Date(ts).toLocaleString('fr-FR');
    }
}
